import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cropWindowSize } from './adImageCropMath';
import './PanCropViewport.css';

/**
 * PanCropViewport — one aspect ratio's pannable crop frame
 * (specs/completed/ad-image-aspect-variants.md).
 *
 * Renders a fixed-aspect box; inside it, the base image scaled so the crop
 * window exactly fills the frame, translated by the current pan. Pointer
 * drag pans it. Emits {pan_x, pan_y} clamped to [0,1].
 *
 * The degenerate axis (panAxis === 'none') makes the whole frame inert —
 * there is nothing to pan (the crop window already spans the full base on
 * both axes, e.g. Square 1:1 on today's square base).
 *
 * `children`, when passed, replaces the plain base-image visual with an
 * absolutely-positioned overlay filling the frame exactly (pointer-events
 * disabled so drag/click still reach the frame's own handlers) — this is
 * how TextOverlayModal combines this component's pan/crop INTERACTION with
 * its own live text-overlay canvas RENDER into one visual pane instead of
 * two stacked ones. The frame's own sizing (FRAME_WIDTH/frameHeight,
 * already ResizeObserver-responsive) becomes the single source of truth
 * the overlay is stretched to fill via width:100%/height:100%, so it can
 * never overflow independently of the frame.
 */

export interface PanCropViewportProps {
    imageUrl: string;
    /** Real pixel dimensions of the base image (from the loaded <img>'s
     *  naturalWidth/naturalHeight — never trust a declared spec size). */
    srcWidth: number;
    srcHeight: number;
    targetWidth: number;
    targetHeight: number;
    panX: number;
    panY: number;
    panAxis: 'x' | 'y' | 'none';
    onPanChange: (panX: number, panY: number) => void;
    disabled?: boolean;
    ariaLabelRatio: string;
    /** Replaces the plain base-image visual — see class doc comment above. */
    children?: React.ReactNode;
}

const MAX_FRAME_WIDTH = 320;
const MIN_FRAME_WIDTH = 200;

const PanCropViewport: React.FC<PanCropViewportProps> = ({
    imageUrl, srcWidth, srcHeight, targetWidth, targetHeight,
    panX, panY, panAxis, onPanChange, disabled, ariaLabelRatio, children,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState(false);
    const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

    // The pointer-drag math below converts a screen-pixel delta into a pan
    // delta using this same width, so the frame's actual rendered width must
    // always match it exactly — a CSS max-width alone would silently desync
    // drag precision from the visible frame on a narrow viewport (e.g. the
    // ≤400px Z Fold breakpoint). Measure the available width instead and
    // clamp the frame to it, same JS-driven-dimension pattern as Layout.tsx's
    // windowWidth state (see CLAUDE.md Mobile & Responsive Architecture).
    const [measuredWidth, setMeasuredWidth] = useState(MAX_FRAME_WIDTH);
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width;
            if (w) setMeasuredWidth(Math.max(MIN_FRAME_WIDTH, Math.min(MAX_FRAME_WIDTH, Math.floor(w))));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Maximal crop window of the target ratio inside the source — see
    // adImageCropMath.ts for the drift note.
    const targetRatio = targetWidth / targetHeight;
    const [cropW, cropH] = cropWindowSize(srcWidth, srcHeight, targetWidth, targetHeight);

    // Display scale: the crop window fills the frame exactly, so the whole
    // image is scaled by frameEdge / cropEdge. Frame aspect === target aspect.
    const FRAME_WIDTH = measuredWidth;
    const frameHeight = Math.round(FRAME_WIDTH / targetRatio);
    const scale = FRAME_WIDTH / cropW;
    const displayedImgW = srcWidth * scale;
    const displayedImgH = srcHeight * scale;

    // Image position within the frame, in display pixels, derived from pan.
    const travelX = displayedImgW - FRAME_WIDTH;
    const travelY = displayedImgH - frameHeight;
    const offsetX = -panX * travelX;
    const offsetY = -panY * travelY;

    const commitDelta = useCallback((dxPx: number, dyPx: number) => {
        if (!dragStartRef.current) return;
        const { panX: startPanX, panY: startPanY } = dragStartRef.current;
        // Convert a display-pixel delta into a pan delta by dividing by the
        // pannable travel in display pixels (the display-space analogue of
        // src_w - cw / src_h - ch).
        const nextPanX = panAxis === 'x' && travelX > 0
            ? Math.max(0, Math.min(1, startPanX - dxPx / travelX))
            : startPanX;
        const nextPanY = panAxis === 'y' && travelY > 0
            ? Math.max(0, Math.min(1, startPanY - dyPx / travelY))
            : startPanY;
        onPanChange(nextPanX, nextPanY);
    }, [panAxis, travelX, travelY, onPanChange]);

    const handlePointerDown = (e: React.PointerEvent) => {
        if (disabled || panAxis === 'none') return;
        (e.target as Element).setPointerCapture(e.pointerId);
        dragStartRef.current = { x: e.clientX, y: e.clientY, panX, panY };
        setDragging(true);
    };
    const handlePointerMove = (e: React.PointerEvent) => {
        if (!dragStartRef.current) return;
        commitDelta(e.clientX - dragStartRef.current.x, e.clientY - dragStartRef.current.y);
    };
    const endDrag = (e: React.PointerEvent) => {
        if (dragStartRef.current) {
            try { (e.target as Element).releasePointerCapture(e.pointerId); } catch { /* no-op */ }
        }
        dragStartRef.current = null;
        setDragging(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (disabled || panAxis === 'none') return;
        const step = e.shiftKey ? 0.05 : 0.01;
        if (panAxis === 'x' && e.key === 'ArrowLeft') { onPanChange(Math.max(0, panX - step), panY); e.preventDefault(); }
        else if (panAxis === 'x' && e.key === 'ArrowRight') { onPanChange(Math.min(1, panX + step), panY); e.preventDefault(); }
        else if (panAxis === 'y' && e.key === 'ArrowUp') { onPanChange(panX, Math.max(0, panY - step)); e.preventDefault(); }
        else if (panAxis === 'y' && e.key === 'ArrowDown') { onPanChange(panX, Math.min(1, panY + step)); e.preventDefault(); }
    };

    const pct = panAxis === 'x' ? Math.round(panX * 100) : Math.round(panY * 100);
    const ariaLabel = panAxis === 'none'
        ? `Crop position for ${ariaLabelRatio} — uses the full image`
        : `Crop position for ${ariaLabelRatio}, ${pct}% from ${panAxis === 'x' ? 'left' : 'top'}`;

    return (
        <div ref={containerRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div
                ref={frameRef}
                className={`pan-crop-frame${panAxis === 'none' ? ' pan-crop-locked' : ''}${dragging ? ' dragging' : ''}`}
                style={{
                    width: FRAME_WIDTH,
                    height: frameHeight,
                    cursor: panAxis === 'none' ? 'default' : (dragging ? 'grabbing' : 'grab'),
                }}
                tabIndex={panAxis === 'none' || disabled ? -1 : 0}
                role="application"
                aria-label={ariaLabel}
                onKeyDown={handleKeyDown}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
            >
                {children ? (
                    <div className="pan-crop-overlay">{children}</div>
                ) : (
                    <img
                        src={imageUrl}
                        alt=""
                        className="pan-crop-img"
                        draggable={false}
                        style={{
                            width: displayedImgW,
                            height: displayedImgH,
                            transform: `translate(${offsetX}px, ${offsetY}px)`,
                        }}
                    />
                )}
            </div>
            <p className="pan-crop-caption">
                {panAxis === 'none'
                    ? 'This ratio uses the full image — nothing to pan.'
                    : panAxis === 'x'
                        ? 'Drag left/right to reposition.'
                        : 'Drag up/down to reposition.'}
            </p>
        </div>
    );
};

export default PanCropViewport;
